'use client';

import { useMemo, useState, type MouseEvent } from 'react';
import {
  filterTenants,
  loadFavorites,
  loadRecents,
  markRecent,
  sortTenants,
  toggleFavorite,
  type TenantEntry,
} from '@/src/lib/tenant-switcher-storage';

const RECENT_SORT_BASE = Date.parse('9999-12-31T23:59:59.999Z');

export interface EnhancedTenantSwitcherProps {
  tenants: TenantEntry[];
  activeTenantId: string;
  onSwitch: (tenantId: string) => void;
}

export function EnhancedTenantSwitcher({
  tenants: initial,
  activeTenantId,
  onSwitch,
}: EnhancedTenantSwitcherProps) {
  const [favoriteIds, setFavoriteIds] = useState(loadFavorites);
  const [recentIds, setRecentIds] = useState(loadRecents);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const tenants = useMemo(
    () =>
      initial.map((tenant) => {
        const recentIndex = recentIds.indexOf(tenant.id);
        return {
          ...tenant,
          isFavorite: favoriteIds.includes(tenant.id),
          lastUsed:
            recentIndex >= 0
              ? new Date(RECENT_SORT_BASE - recentIndex).toISOString()
              : tenant.lastUsed,
        };
      }),
    [favoriteIds, initial, recentIds],
  );

  const active = tenants.find((tenant) => tenant.id === activeTenantId);

  const visible = useMemo(() => {
    const filtered = filterTenants(tenants, query);
    return sortTenants(filtered);
  }, [query, tenants]);

  function handleSwitch(id: string) {
    markRecent(id);
    setRecentIds((previous) => [id, ...previous.filter((recentId) => recentId !== id)].slice(0, 5));
    setIsOpen(false);
    setQuery('');
    onSwitch(id);
  }

  function handleToggleFavorite(id: string, event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    const isFavorite = toggleFavorite(id);
    setFavoriteIds((previous) =>
      isFavorite
        ? previous.includes(id)
          ? previous
          : [...previous, id]
        : previous.filter((favoriteId) => favoriteId !== id),
    );
  }

  if (!active) return null;

  return (
    <div className="relative" data-tour="enhanced-tenant-switcher">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={`Mandant wechseln: ${active.name}`}
        className="inline-flex items-center gap-2 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-3 py-1.5 text-xs font-semibold transition hover:border-[var(--k-color-border-strong)]"
      >
        <span
          aria-hidden="true"
          className="flex h-5 w-5 items-center justify-center rounded bg-[var(--k-color-accent)] text-[10px] font-bold text-white"
        >
          {active.name[0]}
        </span>
        <span className="hidden max-w-48 truncate sm:inline">{active.name}</span>
        <span aria-hidden="true" className="text-[10px]">
          ▾
        </span>
      </button>

      {isOpen ? (
        <div
          role="dialog"
          aria-label="Mandant wechseln"
          className="absolute right-0 top-full z-50 mt-1 w-80 rounded-[var(--k-radius-lg)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] p-2 shadow-[var(--k-shadow-md)]"
        >
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Mandant suchen…"
            aria-label="Mandant-Suche"
            data-tour="tenant-search"
            className="w-full rounded-md border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] px-3 py-1.5 text-sm"
            autoFocus
          />
          <ul role="list" className="mt-2 max-h-64 overflow-y-auto">
            {visible.length === 0 ? (
              <li className="px-3 py-2 text-center text-xs text-[var(--k-color-text-muted)]">
                Keine Mandanten gefunden.
              </li>
            ) : (
              visible.map((tenant) => (
                <li key={tenant.id}>
                  <div
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition ${
                      tenant.id === activeTenantId
                        ? 'bg-[var(--k-color-accent-soft)]'
                        : 'hover:bg-[var(--k-color-surface-muted)]'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleSwitch(tenant.id)}
                      aria-current={tenant.id === activeTenantId ? 'true' : undefined}
                      data-tour={`tenant-option-${tenant.id}`}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span
                        aria-hidden="true"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[var(--k-color-surface-muted)] text-xs font-bold text-[var(--k-color-text-secondary)]"
                      >
                        {tenant.name[0]}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{tenant.name}</span>
                        <span className="block text-[10px] text-[var(--k-color-text-muted)]">
                          {tenant.role}
                          {tenant.legalForm ? ` · ${tenant.legalForm}` : ''}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={(event) => handleToggleFavorite(tenant.id, event)}
                      aria-label={
                        tenant.isFavorite
                          ? 'Aus Favoriten entfernen'
                          : 'Zu Favoriten hinzufügen'
                      }
                      data-tour={`tenant-favorite-${tenant.id}`}
                      className="cursor-pointer p-1 text-sm"
                    >
                      {tenant.isFavorite ? '★' : '☆'}
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
